export interface Bonus {
  number: string
  title: string
  tagline: string
  description: string
  bullets: string[]
  closing: string
  image: string
  alt: string
}

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: '52-Week Yoga Teaching Calendar™',
    tagline: 'Your year of teaching inspiration — already organized.',
    description:
      '52 weeks. 52 directions for your teaching. Instead of arriving at another Monday wondering how to keep your classes fresh, use the calendar to create rhythm and variety across your teaching year.',
    bullets: [
      'Weekly teaching inspiration',
      'Class focus ideas',
      'Intentions',
      'Seasonal variety',
      'Reflection prompts',
      'Easy integration with your Vault classes',
    ],
    closing:
      'Choose the week. Choose the corresponding focus. Find a class. Teach.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fc1a0db1-0787-4374-a222-ee4c3f17cd8a.jpg',
    alt: 'Bonus 01 book cover: the 52-Week Yoga Teaching Calendar™',
  },
  {
    number: '02',
    title: 'Ultimate Yoga Cueing Vault™',
    tagline: 'When you know the pose — but want better words.',
    description:
      'A practical collection of language you can use throughout class, from the opening moments to the final rest.',
    bullets: [
      'Openings',
      'Breath',
      'Pose entry',
      'Alignment-oriented guidance',
      'Transitions',
      'Standing work',
      'Balance',
      'Floor work',
      'Rest',
      'Closing moments',
    ],
    closing:
      'You don’t need to invent every sentence while you’re teaching. Review the cues, choose the ones that fit, then make them your own.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e107a2f1-7db9-4cc5-b99f-c5b4229582ca.jpg',
    alt: 'Bonus 02 book cover: Ultimate Yoga Cueing Vault™',
  },
  {
    number: '03',
    title: 'The Modification Matrix™',
    tagline: 'One pose. Multiple pathways.',
    description:
      'A visual quick-reference guide to range, props, wall and chair options — for the moment you look around the room and realize not everyone needs the same version.',
    bullets: [
      'Beginner-friendly options',
      'Reduced range of movement',
      'Prop variations',
      'Wall support',
      'Chair support',
      'Balance alternatives',
      'Simpler pathways',
      'Mixed-level teaching',
    ],
    closing:
      'Base → Option → Support. You don’t need three separate classes — you need useful pathways inside one class.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d2ac5f5d-1bf3-4dd0-b793-b18279abc43a.jpg',
    alt: 'Bonus 03 book cover: The Modification Matrix™',
  },
  {
    number: '04',
    title: '30 Emergency Ready-to-Teach Classes™',
    tagline: 'The “I need a class NOW” library.',
    description:
      'No time? Substituting? Forgot your plan? Unexpected group? Low energy? No props? Open this bonus — the classes are already organized by situation.',
    bullets: [
      '15–20 minute sessions',
      '30-minute sessions',
      '45-minute options',
      'Standard classes',
      'Beginners',
      'Mixed levels',
      'Gentle groups',
      'Higher-energy groups',
      'Low-energy groups',
      'Mobility',
      'No props',
      'Small spaces',
      'Online teaching',
      'Substitute situations',
      'Unknown groups',
    ],
    closing:
      'Open. Choose. Teach. This may be the bonus you’re happiest to have on the day you need it.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/bb754fbf-edca-436e-847a-2b81b326586c.jpg',
    alt: 'Bonus 04 book cover: 30 Emergency Ready-to-Teach Classes™',
  },
  {
    number: '05',
    title: 'The 60-Second Class Finder™',
    tagline: 'Find the right class without searching the entire Vault.',
    description:
      'The Vault is big, so we created the shortcut. Search by what you actually know about tomorrow’s class.',
    bullets: [
      'Duration',
      'Student level',
      'Style',
      'Intensity',
      'Body focus',
      'Teaching focus',
      'Energy',
      'Props',
      'Room',
      'Teaching situation',
    ],
    closing:
      'Time → Level → Style → Focus → Situation, then Volume → Class → Teach. The goal isn’t more choices — it’s the right choice, faster.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ef0c467b-fa21-4037-a611-759a0ceae410.jpg',
    alt: 'Bonus 05 book cover: The 60-Second Class Finder™',
  },
  {
    number: '06',
    title: 'New Yoga Teacher Confidence Kit™',
    tagline:
      'Everything you wish someone had told you before your first classes.',
    description:
      'You forget the next pose. Students look confused. Someone arrives late. You finish early. This practical guide gives you simple ways to navigate the common teaching moments.',
    bullets: [
      'Pre-class checklist',
      'Opening scripts',
      'Sequence memory system',
      'Teaching checkpoints',
      'Mistake recovery',
      'Mixed-level strategies',
      'Timing rescue',
      'Confidence language',
      'Post-class reset',
      'First 10 Classes Roadmap',
    ],
    closing:
      'Confidence isn’t knowing that nothing will go wrong. It’s knowing what to do next.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3b8701fb-916c-4a38-a2d1-74245c5d60c4.jpg',
    alt: 'Bonus 06 book cover: New Yoga Teacher Confidence Kit™',
  },
  {
    number: '07',
    title: 'Yoga Teacher Planner & Reflection Journal™',
    tagline: 'Plan. Teach. Reflect. Reuse.',
    description:
      'Turn everything inside your Vault into a repeatable teaching system, with practical pages for the whole cycle of a teaching month.',
    bullets: [
      'Monthly & weekly planning',
      'Individual classes',
      'Class architecture',
      'Sequences',
      'Timing',
      'Openings',
      'Cueing notes',
      'Transitions',
      'Modifications',
      'Props',
      'Student observations',
      'Post-class reflection',
      'Class rotation',
      'Favorite classes',
      'Theme tracking',
      'Backup classes',
      'Monthly reviews',
      'Confidence tracking',
    ],
    closing:
      'Plan → Teach → Reflect → Reuse. The class you planned today shouldn’t disappear after you teach it — it should make your next class easier.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/bdb39953-f6ca-4575-aa1c-eaa1c287e54b.jpg',
    alt: 'Bonus 07 book cover: Yoga Teacher Planner & Reflection Journal™',
  },
]
